import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { DataTable } from 'primereact/datatable';
import './DataLibrary.scss';
import { FieldElementSearch } from '../../../../components/list/FieldElementSearch';
import { useLazyGetCatalogQuery } from '../../DataLibrarySlice';
import { NodeDefinition, NodeDefinitions } from '../../../editor/editorSlice';
import { TopBar } from '../../../../components/topBar/topBar';
import { RuntimeMode } from '../../../../app/apiSlice';
import { useAppDispatch } from '../../../../hooks/hooks';
import { Dropdown } from 'primereact/dropdown';
import { setSidebar } from '../../../../app/appSlice';
import { Button } from 'primereact/button';
import ImportFunction from './ImportFunction';
import { DataLibraryFunctionsList } from './DataLibraryFunctionsList';
import { useSecureApplication } from '../../../../hooks/KeycloackConnection';
import { LabelsSelectorsProvider } from '@kosmos/shared';

export const renderLoading = () => {
    const skeleton = () => (
        <Column
            header={<Skeleton width="100%" height="20px"/>}
            body={<Skeleton className="m-2" width="80%" height="20px"/>}
        />
    );
    return (
        <DataTable value={[[], [], [], [], []]}>
            {Array(5).fill(1).map(skeleton)}
        </DataTable>
    );
};

interface IDropdown {
    name: string;
    code?: string;
}

type BricksDefinitions = {
    [p:string]: NodeDefinition[];
}

export const DataLibraryFunctionsPage = () => {
    const {t} = useTranslation();
    const [getCatalog, {data: catalog, ...statusCatalog}] =
        useLazyGetCatalogQuery();
    const [search, setSearch] = useState<string>('');
    const [ascendingSort, setAscendingSort] = useState<boolean>(true);
    const [bricks, setBricks] = useState<NodeDefinitions>();
    const {t: translate} = useTranslation();
    const dispatch = useAppDispatch();
    const {hasAnyPerm} = useSecureApplication();


    let modes: IDropdown[] = [{name: 'Toutes', code: Object.values(RuntimeMode).toString()}];
    Object.keys(RuntimeMode).forEach((runtimeMode) => {
        modes.push({
            name: translate('topology.' + runtimeMode.toLowerCase()),
            code: runtimeMode.toLowerCase(),
        });
    });
    const [modeSelected, setSelectedMode] = useState<IDropdown>(modes[0]);

    useEffect(() => {
        getCatalog({runtimeType: modeSelected.code},true);
    }, [modeSelected]);
    useEffect(() => {
        if (!catalog) {
          setBricks({ sources: [], functions: [], sinks: [] });
          return;
        }
        const translations = new Map<string, Map<string, string>>();

        if (catalog.labels?.values) {
          catalog.labels.values.forEach((label: any) => {
            if (!label.id || !label.i18n) return;

            let frValues: Array<{ key: string; value: string }> = [];
            try {
              const i18n = typeof label.i18n === "string" ? JSON.parse(label.i18n) : label.i18n;
              frValues = i18n["fr-FR"]?.values || [];
            } catch {
              return;
            }

            const map = new Map<string, string>();
            frValues.forEach((item: any) => {
              if (item.key && item.value) {
                map.set(item.key.toLowerCase(), item.value);
              }
            });

            if (map.size > 0) {
              translations.set(label.id.toLowerCase(), map);
            }
          });
        }

        const translateValue = (labelId: string, raw: string): string => {
          const map = translations.get(labelId.toLowerCase());
          return map?.get(raw.toLowerCase()) ?? raw;
        };

        const enrichWithTranslatedMetadatas = (node: NodeDefinition): NodeDefinition => {
          if (!node.metadatas || typeof node.metadatas !== "object") {
            return node;
          }

          const translated: Record<string, string[]> = {};

          Object.entries(node.metadatas).forEach(([labelId, rawValues]) => {
            const values = Array.isArray(rawValues)
              ? rawValues
              : rawValues != null
                ? [rawValues]
                : [];

            translated[labelId] = values
              .filter((v) => typeof v === "string")
              .map((v) => translateValue(labelId, v));
          });

          return {
            ...node,
            translatedMetadatas: translated,
          };
        };

        const newBricks: NodeDefinitions = { sources: [], functions: [], sinks: [] };

        (["sources", "functions", "sinks"] as const).forEach((kind) => {
          const list = catalog[kind];
          if (Array.isArray(list)) {
            newBricks[kind] = list
              .map(enrichWithTranslatedMetadatas) // ← on ajoute translatedMetadatas
              .filter((node) => node.name.toLowerCase().includes(search.toLowerCase()))
              .sort((a, b) =>
                ascendingSort
                  ? a.name.localeCompare(b.name)
                  : b.name.localeCompare(a.name)
              );
          }
        });

        setBricks(newBricks);
      }, [catalog, search, ascendingSort]);

    const sortButton = () => {
        return (
            <img
                src="/images/icon-sort-az.svg"
                style={
                    ascendingSort
                        ? {
                            backgroundColor: '#ffffff',
                            border: 'solid 1px #d0ddea',
                            transform: 'scaleY(-1)',
                        }
                        : {
                            backgroundColor: '#ffffff',
                            border: 'solid 1px #d0ddea',
                        }
                }
                onClick={() => {
                    setAscendingSort(!ascendingSort);
                }}
            />
        );
    };

    return (
        <div className="w-full">
            <TopBar
                left={<h2>{t('title.data-catalog.functions')}</h2>}
                right={
                    <Button
                        icon="pi pi-upload"
                        label={translate('catalog_functions.title')}
                        onClick={() =>
                            dispatch(
                                setSidebar({
                                    title: translate('catalog_functions.title'),
                                    content: (
                                        <LabelsSelectorsProvider serviceUrl="/api/labels?vstoreobject=udf">
                                            <ImportFunction
                                                onUploadDone={() => getCatalog({runtimeType: modeSelected.code})}/>
                                        </LabelsSelectorsProvider>
                                    ),
                                })
                            )
                        }
                        className="mr-3 mt-2 ml-2 mb-2"
                        disabled={!hasAnyPerm("functions.all","functions.create")}
                    />

                }
            />
            <div>
                <div className="flex p-4">
                    <div className="flex-grow-1 mr-2">
                        <FieldElementSearch
                            value={search}
                            placeholder={'Rechercher'}
                            changeValue={setSearch}
                        />
                    </div>
                    <div className="mr-2">
                        <Dropdown
                            value={modeSelected}
                            options={modes}
                            className="flex align-items-center w-full w-15rem"
                            onChange={(e) => setSelectedMode(e.value)}
                            optionLabel="name"
                            placeholder={'Mode'}
                        />
                    </div>
                    {sortButton()}
                </div>
                {bricks && <DataLibraryFunctionsList bricks={bricks} onRefreshCatalog={() => getCatalog({ runtimeType: modeSelected.code})} />}
            </div>
        </div>
    );
};
